import {
  NAME_FIRST_YEAR,
  NAME_LAST_YEAR,
  TREND_WINDOW,
  type TrendEntry,
  type TrendsPage as TrendsData,
} from "@listou/shared/schemas/prenoms";
import { TrendingDown, TrendingUp } from "lucide-react";

import { nameCards } from "../../lib/api";
import { Legend, MiniChart } from "./charts";
import { BOYS, GIRLS, number, percent } from "./format";
import { Breadcrumbs, card, Faq, NameLink, NamesLayout, PrenomsNav, Source } from "./layout";
import { ShareButton } from "./share";
import {
  breadcrumbJsonLd,
  datasetJsonLd,
  faqJsonLd,
  graph,
  rankingJsonLd,
} from "./structured-data";

const RECENT_FROM = NAME_LAST_YEAR - TREND_WINDOW + 1;
const PREVIOUS_FROM = RECENT_FROM - TREND_WINDOW;
const list = (names: string[]) =>
  names.length < 2 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} et ${names.at(-1) ?? ""}`;
const firstNames = (entries: TrendEntry[], size = 5) =>
  list(entries.slice(0, size).map((entry) => entry.name));

/** Pink for a mostly female name, green for a mostly male one: the card carries no sex label. */
const colorOf = (entry: TrendEntry) => (entry.girlsShare >= 50 ? GIRLS : BOYS);

type Section = {
  key: keyof TrendsData;
  title: string;
  /** Label of the jump link at the top of the page. */
  short: string;
  /** Direction cue, so the figure's color never has to carry it. */
  trend?: "up" | "down";
  intro: string;
  headline: (entry: TrendEntry) => string;
  detail: (entry: TrendEntry) => string;
};
const SECTIONS: Section[] = [
  {
    key: "rising",
    short: "En hausse",
    trend: "up",
    title: "Les prénoms en forte hausse",
    intro: `Ceux qui gagnent le plus de naissances entre ${PREVIOUS_FROM}–${RECENT_FROM - 1} et ${RECENT_FROM}–${NAME_LAST_YEAR}.`,
    headline: (entry) => `+${number(entry.recent - entry.previous)}`,
    detail: (entry) => `${number(entry.previous)} puis ${number(entry.recent)} naissances`,
  },
  {
    key: "falling",
    short: "En recul",
    trend: "down",
    title: "Les prénoms en net recul",
    intro: `Ceux qui perdent le plus de naissances entre ${PREVIOUS_FROM}–${RECENT_FROM - 1} et ${RECENT_FROM}–${NAME_LAST_YEAR}.`,
    headline: (entry) => `−${number(entry.previous - entry.recent)}`,
    detail: (entry) => `${number(entry.previous)} puis ${number(entry.recent)} naissances`,
  },
  {
    key: "recentNames",
    short: "Nouvelle génération",
    title: "Les prénoms de la nouvelle génération",
    intro:
      "Presque tous leurs porteurs sont nés depuis 2010. Classés par naissances sur les cinq dernières années.",
    headline: (entry) => number(entry.recent),
    detail: () => `naissances depuis ${RECENT_FROM}`,
  },
  {
    key: "classics",
    short: "Prénoms d’hier",
    title: "Les prénoms d’hier",
    intro: `Donnés des centaines de milliers de fois depuis ${NAME_FIRST_YEAR}, à peine choisis aujourd’hui.`,
    headline: (entry) => number(entry.total),
    detail: (entry) => `naissances, dont ${number(entry.recent)} depuis ${RECENT_FROM}`,
  },
  {
    key: "rarest",
    short: "Les plus rares",
    title: "Les prénoms les plus rares",
    intro: `Le fichier ne publie que les prénoms donnés au moins 100 fois. Voici ceux qui touchent ce plancher et sont encore donnés.`,
    headline: (entry) => number(entry.total),
    detail: (entry) => `naissances, dont ${number(entry.recent)} depuis ${RECENT_FROM}`,
  },
  {
    key: "balanced",
    short: "Mixtes",
    title: "Les prénoms les plus mixtes",
    intro:
      "Autant donnés aux filles qu’aux garçons, parmi les prénoms de plus de 20 000 naissances.",
    headline: (entry) => `${percent(entry.girlsShare)} de filles`,
    detail: (entry) => `${number(entry.total)} naissances`,
  },
];

const faqFor = (data: TrendsData) => [
  {
    question: "Quels prénoms sont en forte hausse ?",
    answer: `Sur ${RECENT_FROM}–${NAME_LAST_YEAR} par rapport à ${PREVIOUS_FROM}–${RECENT_FROM - 1}, ce sont surtout ${firstNames(data.rising)}.`,
  },
  {
    question: "Quels prénoms sont en train de disparaître ?",
    answer: `Parmi les prénoms qui reculent le plus : ${firstNames(data.falling)}. Les prénoms d’hier comme ${firstNames(data.classics, 4)} restent donnés, mais bien plus rarement qu’avant.`,
  },
  {
    question: "Quels sont les prénoms les plus rares ?",
    answer: `Le fichier de l’Insee ne publie pas les prénoms donnés moins de 100 fois. Parmi ceux qui s’en approchent et sont encore donnés : ${firstNames(data.rarest)}.`,
  },
  {
    question: "Quels prénoms sont à la fois féminins et masculins ?",
    answer: `Les plus équilibrés, parmi les prénoms de plus de 20 000 naissances : ${firstNames(data.balanced)}.`,
  },
];

export const trendsHead = (data: TrendsData) => ({
  title: "Prénoms en hausse, en baisse, rares : les tendances · Listou",
  image: nameCards.page("trends", "og"),
  description: `Quels prénoms montent, lesquels disparaissent, lesquels sont rares ? ${firstNames(data.rising, 3)} progressent le plus. Palmarès d’après les données de l’Insee ${NAME_LAST_YEAR}.`,
  jsonLd: graph(
    breadcrumbJsonLd(
      [{ label: "Prénoms", to: "/prenoms" }, { label: "Tendances" }],
      "/prenoms/tendances",
    ),
    rankingJsonLd("Prénoms en forte hausse", data.rising),
    rankingJsonLd("Prénoms en net recul", data.falling),
    rankingJsonLd("Prénoms les plus rares", data.rarest),
    faqJsonLd(faqFor(data)),
    datasetJsonLd({
      name: "Tendances des prénoms en France",
      description: "Hausses, baisses, prénoms rares et prénoms mixtes.",
      coverage: `${NAME_FIRST_YEAR}/${NAME_LAST_YEAR}`,
    }),
  ),
});

function TrendCard({
  entry,
  section,
  rank,
}: {
  entry: TrendEntry;
  section: Section;
  rank: number;
}) {
  return (
    <li>
      <NameLink
        slug={entry.slug}
        className="block h-full rounded-2xl border border-line bg-surface p-4 transition hover:-translate-y-0.5 hover:border-ink"
      >
        <span className="flex items-baseline justify-between gap-2">
          <span className="font-display text-xl font-bold">{entry.name}</span>
          <span className="text-xs text-muted">{rank}</span>
        </span>
        <MiniChart values={entry.years} color={colorOf(entry)} />
        <span className="mt-2 flex items-center gap-1.5 font-bold">
          {section.trend === "up" && <TrendingUp size={16} aria-hidden="true" />}
          {section.trend === "down" && <TrendingDown size={16} aria-hidden="true" />}
          {section.headline(entry)}
        </span>
        <span className="block text-xs text-muted">{section.detail(entry)}</span>
      </NameLink>
    </li>
  );
}

export function TrendsPage({ data }: { data: TrendsData }) {
  return (
    <NamesLayout>
      <Breadcrumbs items={[{ label: "Prénoms", to: "/prenoms" }, { label: "Tendances" }]} />
      <header className="space-y-6">
        <PrenomsNav
          current="/prenoms/tendances"
          share={
            <ShareButton
              path="/prenoms/tendances"
              title="Ce qui monte, ce qui s’éteint"
              text="Les prénoms qui montent et ceux qui s’éteignent, sur Listou :"
              card={(format) => nameCards.page("trends", format)}
              slug="prenoms-tendances"
            />
          }
        />
        <h1 className="max-w-4xl text-4xl sm:text-6xl">Ce qui monte, ce qui s’éteint</h1>
        <p className="max-w-3xl text-lg text-muted">
          Au-delà du top : les prénoms qui progressent, ceux qui reculent, ceux d’une nouvelle
          génération, les classiques d’autrefois et les plus rares. Chaque courbe retrace la vie du
          prénom de {NAME_FIRST_YEAR} à {NAME_LAST_YEAR}.
        </p>
        <Legend
          items={[
            { label: "Prénom surtout féminin", color: GIRLS },
            { label: "Prénom surtout masculin", color: BOYS },
          ]}
        />
        <ul className="flex flex-wrap gap-2 text-sm">
          {SECTIONS.map((section) => (
            <li key={section.key}>
              <a
                href={`#${section.key}`}
                className="inline-block rounded-full border border-line px-4 py-2 hover:border-ink"
              >
                {section.short}
              </a>
            </li>
          ))}
        </ul>
      </header>
      {SECTIONS.map((section) => (
        <section key={section.key} id={section.key} className={`${card} scroll-mt-6 space-y-5`}>
          <div>
            <h2 className="text-3xl font-bold">{section.title}</h2>
            <p className="mt-2 max-w-3xl text-muted">{section.intro}</p>
          </div>
          <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {data[section.key].map((entry, index) => (
              <TrendCard key={entry.slug} entry={entry} section={section} rank={index + 1} />
            ))}
          </ol>
        </section>
      ))}
      <Faq items={faqFor(data)} />
      <Source />
    </NamesLayout>
  );
}
